import React, { createContext, useContext, useCallback, useEffect, useState } from "react";

// FOLLOW MATCH + NOTIFICATION PREFERENCES
// Follow state and preferences are stored locally (localStorage) behind this
// provider so a future account-backed sync (once the owner supplies the auth
// contract) can replace the storage layer without touching the UI.
// Browser notification capability is handled honestly: we distinguish
// enabled / permission-required / unavailable and never fake delivery success.

export interface FollowedMatch {
  fixtureId: string;
  label: string;         // "Home — Away"
  league?: string | null;
  addedAt: number;
}

export type NotifEventKey =
  | "match_start" | "goal" | "halftime" | "second_half"
  | "signal_created" | "momentum_change" | "result_resolved" | "full_time";

export const NOTIF_EVENTS: NotifEventKey[] = [
  "match_start", "goal", "halftime", "second_half",
  "signal_created", "momentum_change", "result_resolved", "full_time",
];

export type NotifPermission = "granted" | "default" | "denied" | "unsupported";

interface FollowCtx {
  followed: FollowedMatch[];
  isFollowing: (fixtureId: string) => boolean;
  toggleFollow: (m: Omit<FollowedMatch, "addedAt">) => void;
  unfollow: (fixtureId: string) => void;
  prefs: Record<NotifEventKey, boolean>;
  setPref: (k: NotifEventKey, v: boolean) => void;
  permission: NotifPermission;
  requestPermission: () => Promise<void>;
}

const FOLLOW_KEY = "sdip.follow";
const PREFS_KEY = "sdip.notifprefs";

const DEFAULT_PREFS: Record<NotifEventKey, boolean> = {
  match_start: true, goal: true, halftime: false, second_half: false,
  signal_created: true, momentum_change: true, result_resolved: true, full_time: false,
};

function loadFollowed(): FollowedMatch[] {
  try { return JSON.parse(localStorage.getItem(FOLLOW_KEY) || "[]"); } catch { return []; }
}
function loadPrefs(): Record<NotifEventKey, boolean> {
  try {
    const raw = JSON.parse(localStorage.getItem(PREFS_KEY) || "null");
    return raw ? { ...DEFAULT_PREFS, ...raw } : DEFAULT_PREFS;
  } catch { return DEFAULT_PREFS; }
}
function detectPermission(): NotifPermission {
  if (typeof window === "undefined" || !("Notification" in window)) return "unsupported";
  return Notification.permission as NotifPermission;
}

const Ctx = createContext<FollowCtx>({} as FollowCtx);

export const FollowProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [followed, setFollowed] = useState<FollowedMatch[]>(loadFollowed);
  const [prefs, setPrefs] = useState<Record<NotifEventKey, boolean>>(loadPrefs);
  const [permission, setPermission] = useState<NotifPermission>(detectPermission);

  useEffect(() => { try { localStorage.setItem(FOLLOW_KEY, JSON.stringify(followed)); } catch {} }, [followed]);
  useEffect(() => { try { localStorage.setItem(PREFS_KEY, JSON.stringify(prefs)); } catch {} }, [prefs]);

  const isFollowing = useCallback((id: string) => followed.some((m) => m.fixtureId === id), [followed]);

  const toggleFollow = useCallback((m: Omit<FollowedMatch, "addedAt">) => {
    setFollowed((prev) => prev.some((x) => x.fixtureId === m.fixtureId)
      ? prev.filter((x) => x.fixtureId !== m.fixtureId)
      : [{ ...m, addedAt: Date.now() }, ...prev]);
  }, []);

  const unfollow = useCallback((id: string) => setFollowed((prev) => prev.filter((x) => x.fixtureId !== id)), []);

  const setPref = useCallback((k: NotifEventKey, v: boolean) => setPrefs((p) => ({ ...p, [k]: v })), []);

  const requestPermission = useCallback(async () => {
    if (typeof window === "undefined" || !("Notification" in window)) { setPermission("unsupported"); return; }
    try {
      const res = await Notification.requestPermission();
      setPermission(res as NotifPermission);
    } catch { setPermission(detectPermission()); }
  }, []);

  return (
    <Ctx.Provider value={{ followed, isFollowing, toggleFollow, unfollow, prefs, setPref, permission, requestPermission }}>
      {children}
    </Ctx.Provider>
  );
};

export function useFollow() { return useContext(Ctx); }

export const NOTIF_LABELS: Record<NotifEventKey, { tr: string; en: string }> = {
  match_start: { tr: "Maç başladığında", en: "Match starts" },
  goal: { tr: "Gol olduğunda", en: "Goal" },
  halftime: { tr: "İlk yarı bittiğinde", en: "Halftime" },
  second_half: { tr: "İkinci yarı başladığında", en: "Second half" },
  signal_created: { tr: "ATLAS sinyal ürettiğinde", en: "ATLAS signal created" },
  momentum_change: { tr: "Baskı belirgin değiştiğinde", en: "Significant momentum change" },
  result_resolved: { tr: "ATLAS sonucu belli olduğunda", en: "ATLAS result resolved" },
  full_time: { tr: "Maç bittiğinde", en: "Full time" },
};
